'use client';

import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import Image from '@/components/Image';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import { FC } from 'react';
import Link from '../Link';

export interface AiHeroProps {
  title: React.ReactNode;
  description: string;
  heroIconTitle: React.ReactNode;

  links: ButtonWithLinkOrAction[];
}

const AiHero: FC<AiHeroProps> = ({
  title,
  description,
  heroIconTitle,
  links = [],
}) => {
  const { modalActionHandler } = useModalButtonActions();

  return (
    <div className={`bg-violet-900 text-white`}>
      <div className="-mt-header-height-mobile md:-mt-header-height-desktop pt-header-height-mobile md:pt-header-height-desktop">
        <div className="pt-2 md:pt-12 px-container-mobile md:px-container-desktop pb-10">
          <div className="mx-auto max-w-grid grid gap-2 md:gap-3 text-left">
            <div className="flex items-center md:justify-between flex-wrap md:flex-nowrap">
              <div className="block col-start-1 col-end-12 md:col-end-6 md:mr-[100px]">
                <div className="flex mb-[24px]">
                  <div className="flex bg-violet-1000 rounded-[16px] items-center">
                    <div className="flex" id="icon">
                      <span className="flex w-[32px] h-[32px] bg-citron-500 rounded-[50%] justify-center items-center mr-[8px] mt-[2px] mb-[2px] ml-[2px]">
                        <Image src="https://front.com/webassets/rebrand/ai-icon.svg" width={37} height={37} alt="ai icon" />
                      </span>
                    </div>
                    <div className="flex text-p2 text-white font-500 mr-[13px]" id="textHeader">
                      {heroIconTitle}
                    </div>
                  </div>
                </div>
                <h1 className="font-500 tracking-tight text-balance text-h1 md:text-display3 mb-[16px]">{title}</h1>
                <p className="text-violet-200 text-p1 max-w-80 text-pretty mb-[32px]">{description}</p>
                <ul className="flex gap-1 md:gap-2">
                  {links.map((link, index) => {
                    return (
                      <li key={`product-hero-link-${link.title?.replace(/\s+/g, '-') ?? index}`}>
                        {link.href.length ? (
                          <Link
                            className={cn('block button button-size-medium', {
                              'button-primary-white': link.type === 'primary',
                              'button-secondary-citron-300': link.type === 'secondary',
                            })}
                            href={link.href}
                          >
                            {link.title}
                          </Link>
                        ) : (
                          <button
                            className={cn('block button button-size-medium', {
                              'button-primary-citron': link.type === 'primary',
                              'button-secondary-citron-300': link.type === 'secondary',
                            })}
                            onClick={() => {
                              modalActionHandler(link.action);
                            }}
                          >
                            {link.title}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="flex col-start-1 col-end-12 md:col-start-6 justify-end relative z-0 mt-[48px] md:mt-0 md:mr-[-58px]">
                <Image src="https://front.com/assets/pages/product/ai/Front-AI-hero-image.png" width={710} height={541} alt="AI Background" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiHero;
